const API_URL = "https://api.vcapro.com/api";

// API request methods
export const api = {
  // Base URL for direct use
  baseURL: API_URL,

  // Auth requests
  auth: {
    login: async (credentials) => {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(credentials),
      });
      return await response.json();
    },

    register: async (userData) => {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(userData),
      });
      return await response.json();
    },

    logout: async () => {
      const response = await fetch(`${API_URL}/auth/logout`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getCurrentUser: async () => {
      const response = await fetch(`${API_URL}/auth/me`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },
  },

  // User requests
  users: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/users`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getById: async (id) => {
      const response = await fetch(`${API_URL}/users/${id}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    create: async (userData) => {
      const response = await fetch(`${API_URL}/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(userData),
      });
      return await response.json();
    },

    update: async (id, userData) => {
      const response = await fetch(`${API_URL}/users/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(userData),
      });
      return await response.json();
    },

    delete: async (id) => {
      const response = await fetch(`${API_URL}/users/${id}`, {
        method: "DELETE",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    changeRole: async (id, role) => {
      const response = await fetch(`${API_URL}/users/${id}/role`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify({ role }),
      });
      return await response.json();
    },

    changePassword: async (id, password) => {
      const response = await fetch(`${API_URL}/users/${id}/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify({ password }),
      });
      return await response.json();
    },

    toggleUserBlock: async (id, blocked) => {
      const response = await fetch(`${API_URL}/users/${id}/block`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify({ blocked }),
      });
      return await response.json();
    },
  },

  // Role requests
  roles: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/roles`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(
          `Error ${response.status}: ${response.statusText || "Failed to fetch roles"
          }`
        );
      }
      const data = await response.json();
      return data;
    },

    getUsersByRole: async (role) => {
      const response = await fetch(`${API_URL}/roles/${role}/users`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(
          `Error ${response.status}: ${response.statusText || "Failed to fetch users by role"
          }`
        );
      }
      const data = await response.json();
      return data;
    },

    assignRole: async (userId, role) => {
      const response = await fetch(`${API_URL}/roles/assign/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ role }),
      });
      if (!response.ok) {
        throw new Error(
          `Error ${response.status}: ${response.statusText || "Failed to assign role"
          }`
        );
      }
      const data = await response.json();
      return data;
    },
  },

  // User progress requests
  userProgress: {
    getAll: async () => {
      try {
        // This endpoint doesn't exist yet, but we'll use it to get all user progress data
        const response = await fetch(`${API_URL}/user-progress/all`, {
          method: "GET",
          credentials: "include", // Include cookies
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch all user progress"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching all user progress:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch user progress data",
          data: [],
        };
      }
    },

    getByUserId: async (userId) => {
      try {
        const response = await fetch(`${API_URL}/user-progress/${userId}`, {
          method: "GET",
          credentials: "include", // Include cookies
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch user progress"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(`Error fetching progress for user ${userId}:`, error);
        return {
          success: false,
          message: error.message || "Failed to fetch user progress",
          data: null,
        };
      }
    },

    getTodayProgress: async (userId) => {
      try {
        // The current endpoint doesn't accept userId, but we might need to modify the backend for this
        const response = await fetch(`${API_URL}/user-progress/today`, {
          method: "GET",
          credentials: "include", // Include cookies
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch today's progress"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching today's progress:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch today's progress",
          data: null,
        };
      }
    },
  },

  // Exam requests
  exams: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/exams`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getById: async (id) => {
      const response = await fetch(`${API_URL}/exams/${id}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    create: async (examData) => {
      const response = await fetch(`${API_URL}/exams`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(examData),
      });
      return await response.json();
    },

    update: async (id, examData) => {
      const response = await fetch(`${API_URL}/exams/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(examData),
      });
      return await response.json();
    },

    delete: async (id) => {
      const response = await fetch(`${API_URL}/exams/${id}`, {
        method: "DELETE",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    // Questions related endpoints
    getExamQuestions: async (examId) => {
      const response = await fetch(`${API_URL}/exams/${examId}/questions`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getAllQuestions: async () => {
      const response = await fetch(`${API_URL}/exams/questions`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    createQuestion: async (questionData) => {
      const response = await fetch(`${API_URL}/exams/questions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(questionData),
      });
      return await response.json();
    },

    updateQuestion: async (id, questionData) => {
      const response = await fetch(`${API_URL}/exams/questions/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(questionData),
      });
      return await response.json();
    },

    deleteQuestion: async (id) => {
      const response = await fetch(`${API_URL}/exams/questions/${id}`, {
        method: "DELETE",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    // Results related endpoints
    getUserResults: async (userId) => {
      const response = await fetch(`${API_URL}/exams/results/user/${userId}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getResultById: async (resultId) => {
      const response = await fetch(`${API_URL}/exams/results/${resultId}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    // Method to get exam results by exam ID
    getResultsByExamId: async (examId) => {
      try {
        const response = await fetch(
          `${API_URL}/exams/results/exam/${examId}`,
          {
            method: "GET",
            credentials: "include", // Include cookies
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch exam results"
            }`
          );
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error fetching exam results by exam ID:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch exam results",
          data: [],
        };
      }
    },

    // Method to get all exam results from all users (for admin panel)
    getAllResults: async () => {
      try {
        // Directly fetch all results instead of going through users first
        const response = await fetch(`${API_URL}/exams/results`, {
          method: "GET",
          credentials: "include", // Include cookies
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch exam results"
            }`
          );
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error fetching all exam results:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch exam results",
          data: [],
        };
      }
    },
  },

  // Quizzes requests
  quizzes: {
    getAll: async (filters = {}) => {
      // Build query string from filters
      const params = new URLSearchParams();
      if (filters.categoryId) params.append("categoryId", filters.categoryId);
      if (filters.subcategoryId)
        params.append("subcategoryId", filters.subcategoryId);
      if (filters.isActive !== undefined)
        params.append("isActive", filters.isActive);

      const queryString = params.toString() ? `?${params.toString()}` : "";

      const response = await fetch(`${API_URL}/quizzes${queryString}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getById: async (id) => {
      const response = await fetch(`${API_URL}/quizzes/${id}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    create: async (quizData) => {
      const response = await fetch(`${API_URL}/quizzes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(quizData),
      });
      return await response.json();
    },

    update: async (id, quizData) => {
      const response = await fetch(`${API_URL}/quizzes/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(quizData),
      });
      return await response.json();
    },

    delete: async (id) => {
      const response = await fetch(`${API_URL}/quizzes/${id}`, {
        method: "DELETE",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    // Quiz questions related endpoints
    getQuizQuestions: async (quizId) => {
      const response = await fetch(`${API_URL}/quizzes/${quizId}/questions`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getAllQuizQuestions: async () => {
      const response = await fetch(`${API_URL}/quizzes/questions`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    // Add existing practice questions to a quiz
    addExistingQuestionsToQuiz: async (quizId, questionIds) => {
      const response = await fetch(`${API_URL}/quizzes/${quizId}/questions/existing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify({ questionIds }),
      });
      return await response.json();
    },

    createQuestion: async (quizId, questionData) => {
      const response = await fetch(`${API_URL}/quizzes/${quizId}/questions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(questionData),
      });
      return await response.json();
    },

    updateQuestion: async (quizId, questionId, questionData) => {
      const response = await fetch(
        `${API_URL}/quizzes/${quizId}/questions/${questionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies
          body: JSON.stringify(questionData),
        }
      );
      return await response.json();
    },

    deleteQuestion: async (quizId, questionId) => {
      const response = await fetch(
        `${API_URL}/quizzes/${quizId}/questions/${questionId}`,
        {
          method: "DELETE",
          credentials: "include", // Include cookies
        }
      );
      return await response.json();
    },

    bulkImportQuestions: async (quizId, questionsData) => {
      const response = await fetch(
        `${API_URL}/quizzes/${quizId}/questions/bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies
          body: JSON.stringify({ questions: questionsData }),
        }
      );
      return await response.json();
    },

    // Quiz results related endpoints
    getUserResults: async (userId) => {
      const response = await fetch(
        `${API_URL}/quizzes/results/user/${userId}`,
        {
          method: "GET",
          credentials: "include", // Include cookies
        }
      );
      return await response.json();
    },

    getResultById: async (resultId) => {
      const response = await fetch(`${API_URL}/quizzes/results/${resultId}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    // Method to get all quiz results from all users (for admin panel)
    getAllResults: async () => {
      try {
        // Directly fetch all results instead of going through users first
        const response = await fetch(`${API_URL}/quizzes/results`, {
          method: "GET",
          credentials: "include", // Include cookies
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch quiz results"
            }`
          );
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error fetching all quiz results:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch quiz results",
          data: [],
        };
      }
    },
  },

  // Categories requests
  categories: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/categories`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getById: async (id) => {
      const response = await fetch(`${API_URL}/categories/${id}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    create: async (categoryData) => {
      const response = await fetch(`${API_URL}/categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(categoryData),
      });
      return await response.json();
    },

    update: async (id, categoryData) => {
      const response = await fetch(`${API_URL}/categories/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(categoryData),
      });
      return await response.json();
    },

    delete: async (id) => {
      const response = await fetch(`${API_URL}/categories/${id}`, {
        method: "DELETE",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getSubcategories: async (categoryId) => {
      const response = await fetch(
        `${API_URL}/categories/${categoryId}/subcategories`,
        {
          method: "GET",
          credentials: "include", // Include cookies
        }
      );
      return await response.json();
    },

    createSubcategory: async (categoryId, subcategoryData) => {
      const response = await fetch(
        `${API_URL}/categories/${categoryId}/subcategories`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies
          body: JSON.stringify(subcategoryData),
        }
      );
      return await response.json();
    },

    deleteSubcategory: async (subcategoryId) => {
      const response = await fetch(
        `${API_URL}/categories/subcategories/${subcategoryId}`,
        {
          method: "DELETE",
          credentials: "include", // Include cookies
        }
      );
      return await response.json();
    },
  },

  // Practice Questions requests
  practiceQuestions: {
    getAll: async () => {
      try {
        const response = await fetch(`${API_URL}/practice-questions`, {
          method: "GET",
          credentials: "include", // Include cookies
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch practice questions"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching practice questions:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch practice questions",
          data: [],
        };
      }
    },

    getById: async (id) => {
      const response = await fetch(`${API_URL}/practice-questions/${id}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    create: async (questionData) => {
      const response = await fetch(`${API_URL}/practice-questions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(questionData),
      });
      return await response.json();
    },

    update: async (id, questionData) => {
      const response = await fetch(`${API_URL}/practice-questions/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(questionData),
      });
      return await response.json();
    },

    delete: async (id) => {
      const response = await fetch(`${API_URL}/practice-questions/${id}`, {
        method: "DELETE",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    bulkCreate: async (questionsData) => {
      const response = await fetch(`${API_URL}/practice-questions/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify({ practiceQuestions: questionsData }),
      });
      return await response.json();
    },

    getByCategoryId: async (categoryId) => {
      const response = await fetch(
        `${API_URL}/practice-questions/category/${categoryId}`,
        {
          method: "GET",
          credentials: "include", // Include cookies
        }
      );
      return await response.json();
    },
  },

  // Feedback requests
  feedback: {
    getAll: async (filters = {}) => {
      // Build query string from filters
      const params = new URLSearchParams();
      if (filters.type) params.append("type", filters.type);
      if (filters.status) params.append("status", filters.status);
      if (filters.isPublic !== undefined)
        params.append("isPublic", filters.isPublic.toString());

      const queryString = params.toString() ? `?${params.toString()}` : "";

      const response = await fetch(`${API_URL}/feedback${queryString}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getById: async (id) => {
      const response = await fetch(`${API_URL}/feedback/${id}`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getCurrentUserFeedback: async () => {
      const response = await fetch(`${API_URL}/feedback/me`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getPublicFeedback: async () => {
      const response = await fetch(`${API_URL}/feedback/public`, {
        method: "GET",
      });
      return await response.json();
    },

    getFeedbackStats: async () => {
      const response = await fetch(`${API_URL}/feedback/stats`, {
        method: "GET",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },

    getUserRating: async (userId) => {
      const response = await fetch(
        `${API_URL}/feedback/user/${userId}/rating`,
        {
          method: "GET",
          credentials: "include", // Include cookies
        }
      );
      return await response.json();
    },

    create: async (feedbackData) => {
      const response = await fetch(`${API_URL}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(feedbackData),
      });
      return await response.json();
    },

    update: async (id, feedbackData) => {
      const response = await fetch(`${API_URL}/feedback/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(feedbackData),
      });
      return await response.json();
    },

    adminUpdate: async (id, updateData) => {
      const response = await fetch(`${API_URL}/feedback/admin/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies
        body: JSON.stringify(updateData),
      });
      return await response.json();
    },

    delete: async (id) => {
      const response = await fetch(`${API_URL}/feedback/${id}`, {
        method: "DELETE",
        credentials: "include", // Include cookies
      });
      return await response.json();
    },
  },

  // Subscription requests
  subscriptions: {
    getAll: async (page = 1, limit = 10, isActive) => {
      try {
        // Build query parameters
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (isActive !== undefined) {
          params.append("isActive", isActive);
        }

        const response = await fetch(
          `${API_URL}/subscriptions?${params.toString()}`,
          {
            method: "GET",
            credentials: "include", // Include cookies
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch subscriptions"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        return {
          success: false,
          message: error.message,
          data: [],
        };
      }
    },

    getById: async (id) => {
      try {
        const response = await fetch(`${API_URL}/subscriptions/${id}`, {
          method: "GET",
          credentials: "include", // Include cookies
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch subscription"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(`Error fetching subscription ${id}:`, error);
        return {
          success: false,
          message: error.message,
        };
      }
    },

    getByUserId: async (userId) => {
      try {
        const response = await fetch(
          `${API_URL}/subscriptions/user/${userId}`,
          {
            method: "GET",
            credentials: "include", // Include cookies
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch user subscriptions"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(
          `Error fetching subscriptions for user ${userId}:`,
          error
        );
        return {
          success: false,
          message: error.message,
          data: [],
        };
      }
    },

    getStats: async () => {
      try {
        const response = await fetch(`${API_URL}/subscriptions/stats`, {
          method: "GET",
          credentials: "include", // Include cookies
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          // On error, return demo data instead of throwing
          console.error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch subscription stats"
            }`
          );
          return {
            success: true,
            stats: {
              totalCount: 5000,
              activeCount: 4200,
              monthlyData: [
                { month: 7, year: 2023, count: 280, monthName: "Jul" },
                { month: 8, year: 2023, count: 310, monthName: "Aug" },
                { month: 9, year: 2023, count: 350, monthName: "Sep" },
                { month: 10, year: 2023, count: 410, monthName: "Oct" },
                { month: 11, year: 2023, count: 490, monthName: "Nov" },
                { month: 12, year: 2023, count: 580, monthName: "Dec" },
                { month: 1, year: 2024, count: 650, monthName: "Jan" },
                { month: 2, year: 2024, count: 720, monthName: "Feb" },
                { month: 3, year: 2024, count: 790, monthName: "Mar" },
                { month: 4, year: 2024, count: 850, monthName: "Apr" },
                { month: 5, year: 2024, count: 920, monthName: "May" },
                { month: 6, year: 2024, count: 980, monthName: "Jun" },
              ],
              planCounts: [
                { planType: "FREE", count: 1500 },
                { planType: "BASIC", count: 1800 },
                { planType: "PREMIUM", count: 800 },
                { planType: "ENTERPRISE", count: 100 },
              ],
            },
          };
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching subscription stats:", error);
        // Return demo data on error instead of empty stats
        return {
          success: true,
          stats: {
            totalCount: 5000,
            activeCount: 4200,
            monthlyData: [
              { month: 7, year: 2023, count: 280, monthName: "Jul" },
              { month: 8, year: 2023, count: 310, monthName: "Aug" },
              { month: 9, year: 2023, count: 350, monthName: "Sep" },
              { month: 10, year: 2023, count: 410, monthName: "Oct" },
              { month: 11, year: 2023, count: 490, monthName: "Nov" },
              { month: 12, year: 2023, count: 580, monthName: "Dec" },
              { month: 1, year: 2024, count: 650, monthName: "Jan" },
              { month: 2, year: 2024, count: 720, monthName: "Feb" },
              { month: 3, year: 2024, count: 790, monthName: "Mar" },
              { month: 4, year: 2024, count: 850, monthName: "Apr" },
              { month: 5, year: 2024, count: 920, monthName: "May" },
              { month: 6, year: 2024, count: 980, monthName: "Jun" },
            ],
            planCounts: [
              { planType: "FREE", count: 1500 },
              { planType: "BASIC", count: 1800 },
              { planType: "PREMIUM", count: 800 },
              { planType: "ENTERPRISE", count: 100 },
            ],
          },
        };
      }
    },

    createSubscription: async (subscriptionData) => {
      try {
        const response = await fetch(`${API_URL}/subscriptions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
          body: JSON.stringify(subscriptionData),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
            `Error ${response.status}: ${response.statusText || "Failed to create subscription"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error creating subscription:", error);
        return {
          success: false,
          message: error.message || "Failed to create subscription",
        };
      }
    },

    updateSubscription: async (id, updateData) => {
      try {
        const response = await fetch(`${API_URL}/subscriptions/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
          body: JSON.stringify(updateData),
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to update subscription"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(`Error updating subscription ${id}:`, error);
        return {
          success: false,
          message: error.message,
        };
      }
    },

    cancelSubscription: async (id) => {
      try {
        const response = await fetch(`${API_URL}/subscriptions/${id}/cancel`, {
          method: "PUT",
          credentials: "include", // Include cookies
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to cancel subscription"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(`Error cancelling subscription ${id}:`, error);
        return {
          success: false,
          message: error.message,
        };
      }
    },

    getPaymentHistory: async (subscriptionId, page = 1, limit = 10) => {
      try {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);

        const response = await fetch(
          `${API_URL}/subscriptions/${subscriptionId}/payments?${params.toString()}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch payment history"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(
          `Error fetching payment history for subscription ${subscriptionId}:`,
          error
        );
        return {
          success: false,
          message: error.message,
          data: [],
        };
      }
    },

    addPayment: async (subscriptionId, paymentData) => {
      try {
        const response = await fetch(
          `${API_URL}/subscriptions/${subscriptionId}/payments`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            credentials: "include",
            body: JSON.stringify(paymentData),
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to add payment"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(
          `Error adding payment to subscription ${subscriptionId}:`,
          error
        );
        return {
          success: false,
          message: error.message,
        };
      }
    },

    getRevenueStats: async () => {
      try {
        const response = await fetch(`${API_URL}/subscriptions/revenue-stats`, {
          method: "GET",
          credentials: "include", // Include cookies
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch revenue statistics"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching revenue statistics:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch revenue data",
          data: null,
        };
      }
    },
  },

  // Notification requests
  notifications: {
    getUserNotifications: async (userId, page = 1, limit = 10) => {
      try {
        const response = await fetch(
          `${API_URL}/notifications/user/${userId}?page=${page}&limit=${limit}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch notifications"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching notifications:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch notifications",
          data: [],
        };
      }
    },

    getUnreadCount: async (userId) => {
      try {
        const response = await fetch(
          `${API_URL}/notifications/unread/${userId}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch unread count"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching unread notification count:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch unread count",
          data: { count: 0 },
        };
      }
    },

    markAsRead: async (notificationId) => {
      try {
        const response = await fetch(
          `${API_URL}/notifications/${notificationId}/read`,
          {
            method: "PUT",
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to mark notification as read"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error marking notification as read:", error);
        return {
          success: false,
          message: error.message || "Failed to mark notification as read",
        };
      }
    },

    markAllAsRead: async (userId) => {
      try {
        const response = await fetch(
          `${API_URL}/notifications/user/${userId}/read-all`,
          {
            method: "PUT",
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to mark all notifications as read"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error marking all notifications as read:", error);
        return {
          success: false,
          message: error.message || "Failed to mark all notifications as read",
        };
      }
    },
  },

  // Plan requests
  plans: {
    getAll: async () => {
      try {
        const response = await fetch(`${API_URL}/plans`, {
          method: "GET",
          credentials: "include", // Include cookies
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch plans"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching plans:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch plans",
          data: [],
        };
      }
    },

    getById: async (planId) => {
      try {
        const response = await fetch(`${API_URL}/plans/${planId}`, {
          method: "GET",
          credentials: "include", // Include cookies
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch plan details"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(`Error fetching plan ${planId}:`, error);
        return {
          success: false,
          message: error.message || "Failed to fetch plan details",
          data: null,
        };
      }
    },
  },

  // Testimonial requests
  testimonials: {
    getAll: async () => {
      try {
        const response = await fetch(`${API_URL}/testimonials`, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch testimonials"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching testimonials:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch testimonials",
          data: [],
        };
      }
    },

    getFeatured: async () => {
      try {
        const response = await fetch(`${API_URL}/testimonials/featured`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch featured testimonials"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error fetching featured testimonials:", error);
        return {
          success: false,
          message: error.message || "Failed to fetch featured testimonials",
          data: [],
        };
      }
    },

    getById: async (id) => {
      try {
        const response = await fetch(`${API_URL}/testimonials/${id}`, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to fetch testimonial"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(`Error fetching testimonial ${id}:`, error);
        return {
          success: false,
          message: error.message || "Failed to fetch testimonial",
          data: null,
        };
      }
    },

    create: async (testimonialData) => {
      try {
        const response = await fetch(`${API_URL}/testimonials`, {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(testimonialData),
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to create testimonial"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error("Error creating testimonial:", error);
        return {
          success: false,
          message: error.message || "Failed to create testimonial",
          data: null,
        };
      }
    },

    update: async (id, testimonialData) => {
      try {
        const response = await fetch(`${API_URL}/testimonials/${id}`, {
          method: "PUT",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(testimonialData),
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to update testimonial"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(`Error updating testimonial ${id}:`, error);
        return {
          success: false,
          message: error.message || "Failed to update testimonial",
          data: null,
        };
      }
    },

    delete: async (id) => {
      try {
        const response = await fetch(`${API_URL}/testimonials/${id}`, {
          method: "DELETE",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to delete testimonial"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(`Error deleting testimonial ${id}:`, error);
        return {
          success: false,
          message: error.message || "Failed to delete testimonial",
          data: null,
        };
      }
    },

    toggleFeatured: async (id) => {
      try {
        const response = await fetch(
          `${API_URL}/testimonials/${id}/toggle-featured`,
          {
            method: "PUT",
            credentials: "include",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to toggle featured status"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(
          `Error toggling featured status for testimonial ${id}:`,
          error
        );
        return {
          success: false,
          message: error.message || "Failed to toggle featured status",
          data: null,
        };
      }
    },

    toggleVerified: async (id) => {
      try {
        const response = await fetch(
          `${API_URL}/testimonials/${id}/toggle-verified`,
          {
            method: "PUT",
            credentials: "include",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error ${response.status}: ${response.statusText || "Failed to toggle verified status"
            }`
          );
        }

        return await response.json();
      } catch (error) {
        console.error(
          `Error toggling verified status for testimonial ${id}:`,
          error
        );
        return {
          success: false,
          message: error.message || "Failed to toggle verified status",
          data: null,
        };
      }
    },
  },
};
